import { DocumentDuplicateIcon } from '@heroicons/react/outline';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { DnsRecord } from '@/models/emailSendingDomains';
import { DnsRecordPurposeKey } from './domainInfoFlowMapper';
import { getRecordStatusBadgeClass } from './domainUtils';

const RECORD_PURPOSE_KEYS: DnsRecordPurposeKey[] = ['emailRouting', 'dkimSigning', 'dmarc', 'linkTracking'];

const getRecordPurposeLabel = (label: string | undefined, t: (key: string) => string): string | undefined => {
  if (!label) {
    return undefined;
  }

  if (RECORD_PURPOSE_KEYS.includes(label as DnsRecordPurposeKey)) {
    return t(`emailWorkspace.domains.recordPurpose.${label}`);
  }

  return label;
};

type CopyFieldProps = {
  label: string;
  value: string;
};

const CopyField: FC<CopyFieldProps> = ({ label, value }) => {
  const { t } = useTranslation('campaign');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className='flex items-center gap-4'>
      <p className='w-14 shrink-0 text-xs font-semibold uppercase tracking-[0.12em] text-quaternary-500'>{label}</p>
      <div className='min-w-0 flex-1 rounded-lg border border-secondary-border bg-gray-50 px-3 py-2.5'>
        <code className='block break-all text-sm text-slate-900'>{value}</code>
      </div>
      <button
        type='button'
        onClick={handleCopy}
        className='inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-secondary-border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-gray-50'
      >
        <DocumentDuplicateIcon className='size-4' aria-hidden='true' />
        {copied ? t('emailWorkspace.domains.copied') : t('emailWorkspace.domains.copy')}
      </button>
    </div>
  );
};

type EmailSendingDomainRecordCardProps = {
  record: DnsRecord;
  index: number;
};

export const EmailSendingDomainRecordCard: FC<EmailSendingDomainRecordCardProps> = ({ record, index }) => {
  const { t } = useTranslation('campaign');
  const purposeLabel = getRecordPurposeLabel(record.label, t);

  return (
    <div className='overflow-hidden rounded-xl border border-secondary-border bg-white'>
      <div className='flex flex-wrap items-center gap-3 border-b border-secondary-border px-4 py-3'>
        <span className='text-sm font-bold text-slate-900'>{t('emailWorkspace.domains.recordLabel', { index: index + 1 })}</span>
        {record.type ? (
          <span className='rounded-md bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600'>{record.type}</span>
        ) : null}
        {purposeLabel ? <span className='text-sm font-medium text-secondary-600'>{purposeLabel}</span> : null}
        {record.required ? (
          <span className='rounded-md bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700'>{t('emailWorkspace.domains.recordRequired')}</span>
        ) : null}
        <span className={twMerge('ml-auto rounded-md px-2.5 py-1 text-xs font-semibold', getRecordStatusBadgeClass(record.status))}>
          {t(`emailWorkspace.domains.recordStatus.${record.status}`)}
        </span>
      </div>
      <div className='space-y-4 p-4 md:px-5 md:py-4'>
        <CopyField label={t('emailWorkspace.domains.host')} value={record.host} />
        <CopyField label={t('emailWorkspace.domains.value')} value={record.value} />
      </div>
    </div>
  );
};
